import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UsePaginationReturn } from '@/hooks/usePagination';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export interface TablePaginationProps {
  pagination: UsePaginationReturn;
  rowsPerPageLabel: string;
  pageOfLabel: string;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

export function TablePagination({
  pagination,
  rowsPerPageLabel,
  pageOfLabel,
  onRefresh,
  isRefreshing = false,
}: TablePaginationProps): JSX.Element | null {
  const { t } = useTranslation();
  const {
    page,
    limit,
    total,
    pageNumbers,
    hasNextPage,
    hasPrevPage,
    goToPage,
    setLimit,
    pageSizeOptions,
  } = pagination;

  if (total === 0) {
    return null;
  }

  const handleRefresh = (): void => {
    if (onRefresh) void onRefresh();
  };

  return (
    <div className="flex flex-nowrap items-center gap-4 border-t border-border bg-muted/50 px-4 py-4 dark:bg-card/80">
      <div className="flex flex-1 items-center gap-4 min-w-0">
        {onRefresh != null ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label={t('pages.common.pagination.refresh')}
            title={t('pages.common.pagination.refresh')}
          >
            <RefreshCw
              className={isRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
              aria-hidden
            />
          </Button>
        ) : null}
        <div className="flex items-center gap-2">
          <Label
            htmlFor="rows-per-page"
            className="text-sm text-muted-foreground whitespace-nowrap"
          >
            {rowsPerPageLabel}
          </Label>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger id="rows-per-page" className="h-9 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
          {pageOfLabel}
        </div>
      </div>
      <div className="flex flex-1 justify-center shrink-0">
        <Pagination>
          <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => hasPrevPage && goToPage(page - 1)}
              disabled={!hasPrevPage}
              aria-disabled={!hasPrevPage}
            />
          </PaginationItem>
          {pageNumbers.map((p, idx) =>
            p === 'ellipsis' ? (
              <PaginationItem key={`ellipsis-${idx}`}>
                <span className="flex h-9 w-9 items-center justify-center text-muted-foreground">
                  ...
                </span>
              </PaginationItem>
            ) : (
              <PaginationItem key={p}>
                <PaginationLink
                  isActive={page === p}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => hasNextPage && goToPage(page + 1)}
              disabled={!hasNextPage}
              aria-disabled={!hasNextPage}
            />
          </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
      <div className="flex flex-1 min-w-0" aria-hidden />
    </div>
  );
}
